import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_MAIN = "https://static.prod-images.emergentagent.com/jobs/03845479-68ae-49c1-b5ac-681381ff3f15/images/a4f5d1458ef6af9984b547ec951f74d8ba355eb5569890e7cabb72b21b4ae3d9.jpeg";

const PARTICLES = Array.from({ length: 22 }).map((_, i) => ({
  id: i,
  left: (i * 47) % 100,
  top: (i * 71) % 100,
  size: 2 + ((i * 13) % 5),
  delay: (i % 8) * 0.7,
  dur: 6 + (i % 6),
}));

export default function Hero({ onExplore }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const p = useSpring(scrollYProgress, { stiffness: 80, damping: 30, mass: 0.5 });

  // Subtle cinematic scroll motion (full-bleed editorial background)
  const bgScale = useTransform(p, [0, 1], [1.05, 1.18]);
  const textOpacity = useTransform(p, [0, 0.35], [1, 0]);
  const textY = useTransform(p, [0, 0.35], [0, -60]);
  const bgY = useTransform(p, [0, 1], [0, 90]);
  const sceneFade = useTransform(p, [0.6, 1], [1, 0.5]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[240vh] bg-cream">
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center grain">
        {/* Full-bleed editorial background image */}
        <motion.div style={{ scale: bgScale, y: bgY, opacity: sceneFade }} className="absolute inset-0">
          <img src={HERO_MAIN} alt="Editorial parfum ORÉLIE" className="w-full h-full object-cover object-[center_30%] grayscale" />
        </motion.div>

        {/* Cream blend overlays — softens the image so it merges with the site */}
        <div className="absolute inset-0 bg-cream/38" />
        <div className="absolute inset-0 bg-gradient-to-b from-cream/55 via-cream/10 to-cream" />
        <div className="absolute inset-0 bg-gradient-to-r from-cream/60 via-transparent to-cream/60" />

        {/* Floating particles */}
        {PARTICLES.map((pt) => (
          <motion.span
            key={pt.id}
            className="absolute rounded-full bg-mocha/40"
            style={{ left: `${pt.left}%`, top: `${pt.top}%`, width: pt.size, height: pt.size }}
            animate={{ y: [0, -30, 0], opacity: [0, 0.7, 0] }}
            transition={{ duration: pt.dur, delay: pt.delay, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}

        {/* Hero copy overlay */}
        <motion.div
          style={{ opacity: textOpacity, y: textY }}
          className="absolute top-[24%] inset-x-0 z-20 px-6 pointer-events-none"
        >
          <div className="relative mx-auto max-w-3xl text-center">
          <div className="absolute inset-x-0 -inset-y-12 -z-10" style={{ background: "radial-gradient(ellipse at center, rgba(42,36,33,0.34) 0%, rgba(42,36,33,0.14) 45%, transparent 72%)" }} />
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1.2 }}
            className="font-serif font-medium text-6xl sm:text-7xl lg:text-8xl text-[#F8E6BE] leading-[1.02] tracking-tight text-balance"
            style={{ textShadow: "0 3px 44px rgba(42,36,33,0.85), 0 1px 4px rgba(42,36,33,0.6)" }}
          >
            Esența Eleganței Pure
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
            className="mt-6 text-[#F1E1BB] text-lg sm:text-xl font-medium max-w-xl mx-auto leading-relaxed"
            style={{ textShadow: "0 2px 24px rgba(42,36,33,0.75)" }}
          >
            Parfumuri artizanale create pentru a evoca amintiri de neuitat și emoții profunde.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4 pointer-events-auto"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={onExplore}
              className="px-10 py-4 bg-[#C69E64] text-[#FFF7EA] text-xs uppercase tracking-[0.25em] rounded-sm hover:bg-[#B4894E] transition-all duration-500 hover:tracking-[0.3em] shadow-[0_16px_38px_-10px_rgba(42,36,33,0.7)]"
              style={{ textShadow: "0 0 9px rgba(255,245,224,0.6)" }}
            >
              Descoperă Colecția
            </button>
          </motion.div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          style={{ opacity: textOpacity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-ink-muted"
        >
          <span className="eyebrow text-[0.6rem]">Derulează</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
            <ArrowDown size={16} strokeWidth={1.5} />
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
